<script setup lang="ts">
import { api } from '@/api';
import { type CreateUpdateEnumerationQuantityDto } from '@/api/generator';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { formBackendErrorHandler } from '@/errorHandling';
import { routenames } from '@/router/routenames';
import { useNotificationStore } from '@/stores/notificationStore';
import { useTranslation } from 'i18next-vue';
import { useRouter } from 'vue-router';
import EnumerationForm from './components/EnumerationForm.vue';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import { backendValidator } from '@/validation/validators/backendValidator';

const { t } = useTranslation();

const router = useRouter();

const notificationStore = useNotificationStore();

const initialValues: Partial<CreateUpdateEnumerationQuantityDto> = {
  name: t('Untitled'),
  isAvailable: true,
  setupUsage: {
    isUsageEnabled: false,
    medicalSystemScope: [],
    qaEquipmentScope: [],
    enumerationOptions: [{ id: 0 }],
  },
  evaluationUsage: {
    isUsageEnabled: false,
    medicalSystemScope: [],
    qaEquipmentScope: [],
    enumerationOptions: [{ id: 0 }],
  },
};

const { form } = useVeeForm<CreateUpdateEnumerationQuantityDto>({
  schemaName: 'CreateUpdateEnumerationQuantityDto',
  options: { initialValues },
  mode: 'add',
  customValidateFunctions: [
    backendValidator((createUpdateEnumerationQuantityDto, errorHandlers) =>
      api.enumerationQuantitiesApi.validateEnumerationQuantity(
        {
          createUpdateEnumerationQuantityDto,
        },
        errorHandlers,
      ),
    ),
  ],
});

const { handleSubmit, values } = form;

const submit = handleSubmit(async (createUpdateEnumerationQuantityDto) => {
  // remove empty options before submit when usage is disabled
  [
    createUpdateEnumerationQuantityDto.evaluationUsage,
    createUpdateEnumerationQuantityDto.setupUsage,
  ].forEach((usage) => {
    if (!usage?.isUsageEnabled && usage?.enumerationOptions) {
      usage.enumerationOptions = usage.enumerationOptions.filter((option) => !!option.name);
    }
  });

  const { success, result: enumeration } =
    await api.enumerationQuantitiesApi.createEnumerationQuantity(
      {
        createUpdateEnumerationQuantityDto,
      },
      [formBackendErrorHandler(form)],
    );

  if (success) {
    notificationStore.success(t('EntityAdded', { name: enumeration.name, iuid: enumeration.iuid }));
    await router.push({
      name: routenames.enumerations.overview,
    });
  }
});
</script>

<template>
  <form @submit="submit">
    <BasePage>
      <template #title>
        <BasePageTitle :subtitle="$t('Enumeration_one')" :title="values.name" />
      </template>

      <template #status>
        <BasePageStatus />
      </template>

      <template #actions>
        <PtwCancelButton :to="{ name: routenames.enumerations.overview }" />
        <VeeSubmitButton class="ml-4" />
      </template>

      <EnumerationForm />
    </BasePage>
  </form>
</template>
